// =====================================================================
// CrudTable — tabela generica pras 4 abas CRUD da Fase 3 (S38).
// Suporta: busca client-side, botoes Edit/Delete por linha, FAB "Novo",
// loading state, empty state.
//
// Props:
//   columns: [{ key, label, render?(row), align?, width?, sortable? }]
//   rows: array
//   loading: bool
//   onEdit(row): callback
//   onDelete(row): callback
//   onAdd(): callback
//   searchPlaceholder?: string
//   getRowId?(row): unique id (default: row.id)
//   searchFields?: string[] (keys das colunas pra busca; default: todas)
// =====================================================================
import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import useIsMobile from "../../../utils/useIsMobile";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  Chip
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
  },
  search: { flexGrow: 1, minWidth: 130 },
  countChip: {
    fontWeight: 600,
    background: `${theme.palette.primary.main}15`,
    color: theme.palette.primary.main
  },
  table: {
    "& th": {
      fontWeight: 700,
      fontSize: "0.75rem",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      color: theme.palette.text.secondary,
      background: theme.palette.background.default
    },
    "& td": {
      fontSize: "0.875rem"
    }
  },
  actionsCell: {
    width: 96,
    whiteSpace: "nowrap",
    textAlign: "right"
  },
  actionBtn: {
    padding: 6
  },
  emptyWrap: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6)
  }
}));

const defaultGetRowId = (row) => row?.id;

const CrudTable = ({
  columns,
  rows,
  loading,
  onEdit,
  onDelete,
  onAdd,
  searchPlaceholder = "Buscar…",
  getRowId = defaultGetRowId,
  searchFields = null
}) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!rows) return [];
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    const keys =
      searchFields && searchFields.length > 0
        ? searchFields
        : columns.map((c) => c.key);
    return rows.filter((row) =>
      keys.some((k) => {
        const v = row?.[k];
        if (v == null) return false;
        return String(v).toLowerCase().includes(term);
      })
    );
  }, [rows, q, searchFields, columns]);

  return (
    <div>
      <Box className={classes.toolbar}>
        <TextField
          variant="outlined"
          size="small"
          className={classes.search}
          placeholder={searchPlaceholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        <Chip
          label={`${filtered.length}${q ? ` de ${rows?.length || 0}` : ""}`}
          size="small"
          className={classes.countChip}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onAdd}
        >
          Novo
        </Button>
      </Box>

      {loading ? (
        <div className={classes.loadingWrap}>
          <CircularProgress />
        </div>
      ) : filtered.length === 0 ? (
        <div className={classes.emptyWrap}>
          <Typography variant="body2">
            {q
              ? `Nenhum resultado pra "${q}".`
              : "Nada cadastrado ainda. Clique em Novo pra começar."}
          </Typography>
        </div>
      ) : (
        <div style={{ overflowX: "auto", width: "100%" }}>
        <Table
          size="small"
          className={classes.table}
          style={isMobile ? { minWidth: 640 } : undefined}
        >
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align || "left"}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.label}
                </TableCell>
              ))}
              <TableCell className={classes.actionsCell}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row) => (
              <TableRow key={getRowId(row)} hover>
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align || "left"}>
                    {c.render ? c.render(row) : row?.[c.key] ?? "—"}
                  </TableCell>
                ))}
                <TableCell className={classes.actionsCell}>
                  <IconButton
                    size="small"
                    className={classes.actionBtn}
                    title="Editar"
                    onClick={() => onEdit && onEdit(row)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={classes.actionBtn}
                    title="Excluir"
                    onClick={() => onDelete && onDelete(row)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      )}
    </div>
  );
};

export default CrudTable;
